/**
 * Animate the speaker page's <details> disclosures.
 *
 * The full bio's "Read More" and each speech card's are native
 * <details>/<summary>, which is why they work with no script at all — but a
 * native details snaps open. There is no way to transition that from CSS
 * alone: the browser takes the content out of the layout entirely when closed,
 * so there is nothing to animate from, and `height: auto` is not a value a
 * transition can travel to.
 *
 * So the height is MEASURED and animated, the same approach the quote slider
 * uses. The element stays a real <details> throughout — the summary still
 * toggles it, it still announces its own expanded state, and with this script
 * absent or with reduced motion asked for, the native behaviour is what runs.
 */

const DETAILS = '.dn-speaker__bio-full, .dn-speaker__speech-more';
const DURATION = 320;

/**
 * Whether the visitor has asked for reduced motion.
 *
 * @return {boolean} True when animation should be skipped.
 */
function reducedMotion() {
	return window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
}

/**
 * Animate one element between two heights, then hand height back to the layout.
 *
 * @param {HTMLElement} element The element to animate.
 * @param {number}      from    Starting height in px.
 * @param {number}      to      Target height in px.
 * @param {Function}    done    Called once the travel is over.
 */
function animateHeight( element, from, to, done ) {
	element.style.overflow = 'hidden';
	element.style.height = `${ from }px`;

	// Force the start height to land before the target is set, or the browser
	// coalesces the two and there is nothing to transition between.
	void element.offsetHeight;

	element.style.transition = `height ${ DURATION }ms cubic-bezier(0.4, 0, 0.2, 1)`;
	element.style.height = `${ to }px`;

	const finish = () => {
		element.style.removeProperty( 'height' );
		element.style.removeProperty( 'overflow' );
		element.style.removeProperty( 'transition' );
		element.removeEventListener( 'transitionend', finish );
		done();
	};

	element.addEventListener( 'transitionend', finish );
}

/**
 * Wire one disclosure.
 *
 * @param {HTMLElement} details The <details> element.
 */
function initDetails( details ) {
	const summary = details.querySelector( 'summary' );

	if ( ! summary ) {
		return;
	}

	// Everything after the summary is the panel. Wrapped in one element by the
	// renderer, so there is a single box to size.
	const panel = summary.nextElementSibling;

	if ( ! panel ) {
		return;
	}

	let animating = false;

	summary.addEventListener( 'click', ( event ) => {
		// Let the browser do it: nothing to animate, and the native toggle is
		// the behaviour to fall back to.
		if ( reducedMotion() ) {
			return;
		}

		event.preventDefault();

		if ( animating ) {
			return;
		}

		animating = true;

		if ( details.open ) {
			// Measure before closing — once `open` comes off, the panel is gone
			// from the layout and measures zero.
			const from = panel.getBoundingClientRect().height;

			animateHeight( panel, from, 0, () => {
				details.open = false;
				animating = false;
			} );

			return;
		}

		// Open first so the panel is in the layout to be measured, then travel
		// from nothing to what it turned out to be.
		details.open = true;

		const to = panel.getBoundingClientRect().height;

		animateHeight( panel, 0, to, () => {
			animating = false;
		} );
	} );
}

function init() {
	document.querySelectorAll( DETAILS ).forEach( initDetails );
}

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
